import {
  SEED_CARD_CATALOG,
  STARTER_DECKS,
  applyEvent,
  cloneGameState,
  computeDeckFingerprint,
  createInitialGameState,
  exportDeckToFormat,
  generatePairingQrSvg,
  importUniversalDeck,
  normalizeScryfallApiCard,
  parsePlainTextDeck,
  processIntent,
  projectGameView,
  projectReplayFrameAtStep,
  type AuthProviderId,
  type CardDefinition,
  type ClientGameView,
  type FormatId,
  type GameEvent,
  type GameIntent,
  type GameState,
  type ImportedDeckPayload,
  type RngProvider,
  type SessionType,
  type TableSettings,
} from '#shared';
import {
  BASIC_LAND_NAMES,
  normalizeCardKey,
  optimizeDeckCart,
  reoptimizeAfterStockCheck,
  seedVendorQuotesForItems,
  type CartDeckItem,
} from '#cart-optimizer';
import type {
  AccountProfileDTO,
  DeckPriceAlertDTO,
  SavedDeckDTO,
} from './clientApi.ts';
import { getSessionBaseUrl } from './clientApi.ts';

const PROFILE_STORAGE_KEY = 'playtable_tab_profile_v1';
const TABLES_STORAGE_KEY = 'playtable_local_tables_v1';
const DECKS_STORAGE_KEY = 'playtable_saved_decks_v1';
const ALERTS_STORAGE_KEY = 'playtable_price_alerts_v1';
const CHANNEL_NAME = 'playtable_local_pod_v1';

const SEAT_COLORS = ['#f59e0b', '#38bdf8', '#a855f7', '#10b981', '#f43f5e', '#eab308', '#06b6d4', '#ec4899'];

interface StoredLocalTable {
  tableId: string;
  inviteCode: string;
  overlaySecret: string;
  overlayDelayedHandSeatId: string | null;
  initialState: GameState;
  state: GameState;
  events: GameEvent[];
}

const browserRng: RngProvider = {
  nextInt(maxExclusive: number): number {
    if (maxExclusive <= 1) return 0;
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0] % maxExclusive;
  },
  now(): number {
    return Date.now();
  },
};

function randomId(prefix: string, len = 8): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < len; i++) {
    out += chars[browserRng.nextInt(chars.length)];
  }
  return prefix ? `${prefix}_${out.toLowerCase()}` : out;
}

let channelInstance: BroadcastChannel | null | undefined;

const tableListeners = new Set<(tableId: string) => void>();

function getPodChannel(): BroadcastChannel | null {
  if (channelInstance !== undefined) return channelInstance;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channelInstance = new BroadcastChannel(CHANNEL_NAME);
      channelInstance.onmessage = (ev) => {
        const data = ev.data as { type?: string; tableId?: string } | null;
        if (data?.type === 'TABLE_UPDATED' && data.tableId) {
          for (const fn of tableListeners) fn(data.tableId);
        }
      };
    } else {
      channelInstance = null;
    }
  } catch {
    channelInstance = null;
  }
  return channelInstance;
}

function notifyTableUpdated(tableId: string): void {
  for (const fn of tableListeners) fn(tableId);
  try {
    getPodChannel()?.postMessage({ type: 'TABLE_UPDATED', tableId });
  } catch {
    // ignore channel errors
  }
}

function readLocalProfile(): AccountProfileDTO | null {
  try {
    const raw = sessionStorage.getItem(PROFILE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AccountProfileDTO) : null;
  } catch {
    return null;
  }
}

function writeLocalProfile(profile: AccountProfileDTO | null): void {
  try {
    if (!profile) {
      sessionStorage.removeItem(PROFILE_STORAGE_KEY);
    } else {
      sessionStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    }
  } catch {
    // ignore storage quota errors
  }
}

function ensureLocalProfile(handleHint?: string, provider: AuthProviderId = 'deckstr'): AccountProfileDTO {
  const existing = readLocalProfile();
  if (existing) {
    if (handleHint && handleHint.trim() && existing.displayName !== handleHint.trim()) {
      const updated: AccountProfileDTO = {
        ...existing,
        displayName: handleHint.trim(),
      };
      writeLocalProfile(updated);
      return updated;
    }
    return existing;
  }
  const suffix = 100 + browserRng.nextInt(899);
  const name = handleHint?.trim() || `Planeswalker${suffix}`;
  const created: AccountProfileDTO = {
    accountId: randomId('acc', 10),
    publicId: randomId('pub', 8),
    displayName: name,
    avatarUrl: null,
    profileColor: SEAT_COLORS[browserRng.nextInt(SEAT_COLORS.length)],
    verifiedProvider: provider,
    completedGames: 0,
    leaderboardOptIn: true,
    isStaff: false,
    blockedPublicIds: [],
    linkedProviders: [
      {
        provider,
        providerHandle: name,
        linkedAt: Date.now(),
      },
    ],
    notifications: [],
  };
  writeLocalProfile(created);
  return created;
}

function readAllTables(): Record<string, StoredLocalTable> {
  try {
    const raw = localStorage.getItem(TABLES_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, StoredLocalTable>) : {};
  } catch {
    return {};
  }
}

function writeAllTables(tables: Record<string, StoredLocalTable>): void {
  try {
    // Keep most recent 12 tables so localStorage stays compact
    const entries = Object.entries(tables).sort(
      (a, b) => (b[1].state.updatedAt ?? 0) - (a[1].state.updatedAt ?? 0)
    );
    const trimmed = Object.fromEntries(entries.slice(0, 12));
    localStorage.setItem(TABLES_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // ignore quota errors
  }
}

function readSavedDecks(): SavedDeckDTO[] {
  try {
    const raw = localStorage.getItem(DECKS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedDeckDTO[]) : [];
  } catch {
    return [];
  }
}

function writeSavedDecks(decks: SavedDeckDTO[]): void {
  try {
    localStorage.setItem(DECKS_STORAGE_KEY, JSON.stringify(decks));
  } catch {
    // ignore
  }
}

function readPriceAlerts(): DeckPriceAlertDTO[] {
  try {
    const raw = localStorage.getItem(ALERTS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DeckPriceAlertDTO[]) : [];
  } catch {
    return [];
  }
}

function writePriceAlerts(alerts: DeckPriceAlertDTO[]): void {
  try {
    localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(alerts));
  } catch {
    // ignore
  }
}

function findSeatForAccount(state: GameState, accountId: string | null): string | null {
  if (!accountId) return null;
  for (const sid of state.seatOrder) {
    if (state.seats[sid]?.accountId === accountId) return sid;
  }
  return null;
}

function seatProfileIntoRoom(
  room: StoredLocalTable,
  profile: AccountProfileDTO,
  preferredDeck?: ImportedDeckPayload | null
): string | null {
  const existing = findSeatForAccount(room.state, profile.accountId);
  if (existing) return existing;

  const openSeatId = room.state.seatOrder.find((sid) => room.state.seats[sid]?.accountId === null);
  if (!openSeatId) return null;

  const now = Date.now();
  const joinEv: GameEvent = {
    seq: room.state.seq + 1,
    timestamp: now,
    turnNumber: room.state.turnNumber,
    actorSeatId: openSeatId,
    actorName: profile.displayName,
    type: 'SEAT_JOINED',
    summary: `${profile.displayName} sat in Seat ${room.state.seats[openSeatId].seatIndex + 1}.`,
    payload: {
      seatId: openSeatId,
      accountId: profile.accountId,
      publicId: profile.publicId,
      displayName: profile.displayName,
      avatarUrl: profile.avatarUrl,
      profileColor: profile.profileColor,
      verifiedProvider: profile.verifiedProvider,
    },
  };
  room.state = applyEvent(room.state, joinEv);
  room.events.push(joinEv);

  const starterIdx = room.state.seats[openSeatId].seatIndex % STARTER_DECKS.length;
  const fallbackStarter = room.state.settings.useCommandZone
    ? STARTER_DECKS[starterIdx % 2]
    : STARTER_DECKS[2];
  const deckToLoad =
    preferredDeck &&
    (preferredDeck.mainboard.length > 0 || preferredDeck.commanders.length > 0)
      ? preferredDeck
      : fallbackStarter;

  if (room.state.status === 'lobby') {
    const loadRes = processIntent(
      room.state,
      room.initialState,
      room.events,
      { type: 'LOAD_DECK', deck: deckToLoad, showTransparency: true },
      {
        accountId: profile.accountId,
        seatId: openSeatId,
        isHost: room.state.hostAccountId === profile.accountId,
        isJudge: false,
        displayName: profile.displayName,
        publicId: profile.publicId,
      },
      browserRng
    );
    if (loadRes.ok) {
      room.state = loadRes.state;
      room.events.push(...loadRes.emittedEvents);
    }
  }

  return openSeatId;
}

function deckToCartItems(deck?: ImportedDeckPayload | null, includeSideboard = true): CartDeckItem[] {
  if (!deck) return [];
  const map = new Map<string, CartDeckItem>();
  const addEntry = (card: CardDefinition, qty: number) => {
    if (qty <= 0) return;
    const key = normalizeCardKey(card.name);
    const isBasic =
      BASIC_LAND_NAMES.has(key) || card.typeLine.toLowerCase().includes('basic land');
    const prev = map.get(key);
    if (prev) {
      prev.quantity += qty;
    } else {
      map.set(key, {
        cardName: card.name,
        quantity: qty,
        setCode: card.setCode,
        collectorNumber: card.collectorNumber,
        foil: card.foil,
        isBasicLand: isBasic,
      });
    }
  };
  for (const cmd of deck.commanders) addEntry(cmd, 1);
  for (const m of deck.mainboard) addEntry(m.card, m.quantity);
  if (includeSideboard) {
    for (const s of deck.sideboard) addEntry(s.card, s.quantity);
  }
  return Array.from(map.values());
}

export function getLocalProjectedView(
  tableId: string,
  sessionType: SessionType = 'table',
  overlaySecret?: string | null
): ClientGameView | null {
  const tables = readAllTables();
  const room = tables[tableId];
  if (!room) return null;

  const profile = readLocalProfile();
  const seatId = findSeatForAccount(room.state, profile?.accountId ?? null);
  const effectiveType: SessionType =
    sessionType === 'overlay' && overlaySecret === room.overlaySecret ? 'overlay' : sessionType;

  return projectGameView(room.state, room.events, {
    accountId: profile?.accountId ?? null,
    seatId,
    sessionType: effectiveType,
    spectatorCount: 0,
    overlayDelayedHandSeatId: room.overlayDelayedHandSeatId,
  });
}

export function dispatchLocalSandboxIntent(
  tableId: string,
  intent: GameIntent
): { ok: boolean; error?: string; view?: ClientGameView } {
  const tables = readAllTables();
  const room = tables[tableId];
  if (!room) return { ok: false, error: 'Table not found.' };

  const profile = ensureLocalProfile();
  const seatId = findSeatForAccount(room.state, profile.accountId);
  const res = processIntent(
    room.state,
    room.initialState,
    room.events,
    intent,
    {
      accountId: profile.accountId,
      seatId,
      isHost: room.state.hostAccountId === profile.accountId,
      isJudge: room.state.judgeAccountIds.includes(profile.accountId),
      displayName: profile.displayName,
      publicId: profile.publicId,
    },
    browserRng
  );

  if (!res.ok) {
    return { ok: false, error: res.error };
  }

  room.state = res.state;
  if (res.replacedEventLog) {
    room.events = [...res.replacedEventLog];
  } else {
    room.events.push(...res.emittedEvents);
  }

  tables[tableId] = room;
  writeAllTables(tables);
  notifyTableUpdated(tableId);

  const view = getLocalProjectedView(tableId, 'table');
  return { ok: true, view: view ?? undefined };
}

export function dispatchLocalHostAction(
  tableId: string,
  action: Record<string, unknown>
): void {
  const tables = readAllTables();
  const room = tables[tableId];
  if (!room) return;
  const profile = ensureLocalProfile();
  if (room.state.hostAccountId !== profile.accountId) return;

  if (action.kind === 'LOCK_TABLE' && typeof action.locked === 'boolean') {
    const ev: GameEvent = {
      seq: room.state.seq + 1,
      timestamp: Date.now(),
      turnNumber: room.state.turnNumber,
      actorSeatId: findSeatForAccount(room.state, profile.accountId),
      actorName: profile.displayName,
      type: 'TABLE_LOCK_CHANGED',
      summary: `${profile.displayName} ${action.locked ? 'locked' : 'unlocked'} the table.`,
      payload: { locked: action.locked },
    };
    room.state = applyEvent(room.state, ev);
    room.events.push(ev);
    tables[tableId] = room;
    writeAllTables(tables);
    notifyTableUpdated(tableId);
  }
}

export function subscribeLocalTableUpdates(
  tableId: string,
  onUpdate: () => void
): () => void {
  getPodChannel();
  const handler = (updatedId: string) => {
    if (updatedId === tableId) onUpdate();
  };
  tableListeners.add(handler);
  const storageHandler = (e: StorageEvent) => {
    if (e.key === TABLES_STORAGE_KEY) onUpdate();
  };
  window.addEventListener('storage', storageHandler);
  return () => {
    tableListeners.delete(handler);
    window.removeEventListener('storage', storageHandler);
  };
}

export async function handleLocalSandboxApiRequest<T>(
  rawPath: string,
  method: string,
  body?: any
): Promise<T> {
  const [pathname, queryStr] = rawPath.split('?');
  const qs = new URLSearchParams(queryStr ?? '');

  if (pathname === '/api/health') {
    return { ok: true, devAuthEnabled: true, domain: window.location.host } as T;
  }

  if (pathname === '/api/auth/me') {
    const profile = readLocalProfile();
    return {
      authenticated: Boolean(profile),
      profile,
      csrfToken: profile ? 'local_csrf' : null,
    } as T;
  }

  if (pathname === '/api/auth/dev-login' && method === 'POST') {
    const profile = ensureLocalProfile(body?.handle, body?.provider ?? 'deckstr');
    return {
      authenticated: true,
      profile,
      csrfToken: 'local_csrf',
    } as T;
  }

  if (pathname === '/api/auth/signout' || pathname === '/api/auth/signout-all') {
    writeLocalProfile(null);
    return { ok: true } as T;
  }

  if (pathname === '/api/account/profile' && method === 'PATCH') {
    const cur = ensureLocalProfile();
    const updated: AccountProfileDTO = {
      ...cur,
      ...(body?.displayName ? { displayName: String(body.displayName).trim() } : {}),
      ...(body?.profileColor ? { profileColor: String(body.profileColor) } : {}),
      ...(body?.leaderboardOptIn !== undefined ? { leaderboardOptIn: Boolean(body.leaderboardOptIn) } : {}),
    };
    writeLocalProfile(updated);
    return { profile: updated } as T;
  }

  if (pathname === '/api/tables/public') {
    const tables = Object.values(readAllTables());
    return {
      tables: tables
        .filter((t) => t.state.settings.isPublic)
        .map((t) => {
          const seatsFilled = Object.values(t.state.seats).filter((s) => s.accountId !== null).length;
          return {
            tableId: t.tableId,
            inviteCode: t.inviteCode,
            name: t.state.settings.name,
            format: t.state.settings.format,
            status: t.state.status,
            seatsFilled,
            maxSeats: t.state.settings.maxSeats,
            spectatorsAllowed: t.state.settings.spectatorsAllowed,
            allowLateJoinRequests: t.state.settings.allowLateJoinRequests,
            hasPassword: false,
            isTracked: t.state.settings.isTracked,
            competitiveMode: t.state.settings.competitiveMode,
            bracket: t.state.pregame.ruleZero.targetBracket,
            language: t.state.settings.language ?? 'en',
            description: t.state.settings.description ?? '',
            region: 'local-browser',
          };
        }),
    } as T;
  }

  if (pathname === '/api/tables/mine') {
    const profile = readLocalProfile();
    const tables = Object.values(readAllTables());
    const mine = profile
      ? tables.filter((t) => findSeatForAccount(t.state, profile.accountId) !== null)
      : [];
    return {
      resumable: mine.map((t) => ({
        tableId: t.tableId,
        inviteCode: t.inviteCode,
        name: t.state.settings.name,
        format: t.state.settings.format,
        status: t.state.status,
        turnNumber: t.state.turnNumber,
        updatedAt: t.state.updatedAt,
      })),
      history: [],
    } as T;
  }

  if (pathname === '/api/tables' && method === 'POST') {
    const profile = ensureLocalProfile();
    const format: FormatId = body?.format ?? 'commander';
    const settingsOverrides: Partial<TableSettings> = {
      ...(body?.settings ?? {}),
      ...(body?.quickStartGoldfish ? { maxSeats: 1, name: `${profile.displayName}'s Goldfish` } : {}),
    };
    const tableId = randomId('tbl', 10);
    const inviteCode = randomId('', 6);
    const overlaySecret = randomId('ovl', 16);
    const initial = createInitialGameState({
      tableId,
      inviteCode,
      hostAccountId: profile.accountId,
      format,
      settingsOverrides,
    });
    const room: StoredLocalTable = {
      tableId,
      inviteCode,
      overlaySecret,
      overlayDelayedHandSeatId: null,
      initialState: cloneGameState(initial),
      state: initial,
      events: [],
    };

    const seatId = seatProfileIntoRoom(room, profile, body?.initialDeck ?? null);

    if (body?.quickStartGoldfish && seatId) {
      const startRes = processIntent(
        room.state,
        room.initialState,
        room.events,
        { type: 'START_MATCH', autoShuffle: true },
        {
          accountId: profile.accountId,
          seatId,
          isHost: true,
          isJudge: false,
          displayName: profile.displayName,
          publicId: profile.publicId,
        },
        browserRng
      );
      if (startRes.ok) {
        room.state = startRes.state;
        room.events.push(...startRes.emittedEvents);
      }
    }

    const tables = readAllTables();
    tables[tableId] = room;
    writeAllTables(tables);
    notifyTableUpdated(tableId);

    return {
      tableId,
      inviteCode,
      overlaySecret,
      wsTicket: 'local_ws_ticket',
    } as T;
  }

  if (pathname === '/api/tables/join' && method === 'POST') {
    const profile = ensureLocalProfile();
    const tables = readAllTables();
    const code = String(body?.inviteCode ?? '').trim().toUpperCase();
    const room = body?.tableId
      ? tables[String(body.tableId)]
      : Object.values(tables).find((t) => t.inviteCode.toUpperCase() === code);

    if (!room) {
      throw new Error('Table not found in this browser session.');
    }

    const sessionType: SessionType = body?.asSpectator
      ? 'spectator'
      : (body?.sessionType as SessionType) ?? 'table';

    let seatId: string | null = findSeatForAccount(room.state, profile.accountId);
    if (!body?.asSpectator && !seatId) {
      seatId = seatProfileIntoRoom(room, profile, null);
      tables[room.tableId] = room;
      writeAllTables(tables);
      notifyTableUpdated(room.tableId);
    }

    return {
      tableId: room.tableId,
      inviteCode: room.inviteCode,
      seatId,
      sessionType,
      wsTicket: 'local_ws_ticket',
    } as T;
  }

  const wsTicketMatch = pathname.match(/^\/api\/tables\/([^/]+)\/ws-ticket$/);
  if (wsTicketMatch) {
    const tableId = decodeURIComponent(wsTicketMatch[1]);
    return {
      wsTicket: 'local_ws_ticket',
      tableId,
      sessionType: (body?.sessionType as SessionType) ?? 'table',
    } as T;
  }

  const pairCodeMatch = pathname.match(/^\/api\/tables\/([^/]+)\/pair-code$/);
  if (pairCodeMatch) {
    const tableId = decodeURIComponent(pairCodeMatch[1]);
    const purpose = body?.purpose ?? 'private_hand';
    const code = randomId('', 6);
    const base = String(body?.origin || getSessionBaseUrl());
    const sep = base.includes('?') ? '&' : '?';
    const pairUrl = `${base}${sep}table=${encodeURIComponent(tableId)}&session=private&pair=${code}`;
    return {
      code,
      pairUrl,
      qrSvg: generatePairingQrSvg(pairUrl),
      purpose,
      expiresAt: Date.now() + 5 * 60_000,
    } as T;
  }

  const replayMatch = pathname.match(/^\/api\/tables\/([^/]+)\/replay$/);
  if (replayMatch) {
    const tableId = decodeURIComponent(replayMatch[1]);
    const room = readAllTables()[tableId];
    if (!room) throw new Error('Table not found.');
    const profile = readLocalProfile();
    const seatId = findSeatForAccount(room.state, profile?.accountId ?? null);
    const totalSteps = room.events.length;
    const currentStep = qs.has('step') ? Math.max(0, Math.min(totalSteps, Number(qs.get('step')))) : totalSteps;
    const frame = projectReplayFrameAtStep(room.initialState, room.events, currentStep, {
      accountId: profile?.accountId ?? null,
      seatId,
      sessionType: 'table',
    });
    const milestones = room.events
      .map((ev, i) => ({ step: i + 1, seq: ev.seq, turnNumber: ev.turnNumber, type: ev.type, summary: ev.summary }))
      .filter((m) => ['MATCH_STARTED', 'TURN_PASSED', 'PLAYER_ELIMINATED'].includes(m.type));
    return {
      tableId,
      totalSteps,
      currentStep,
      milestones,
      frame,
    } as T;
  }

  const reportMatch = pathname.match(/^\/api\/tables\/([^/]+)\/report$/);
  if (reportMatch) {
    const tableId = decodeURIComponent(reportMatch[1]);
    const room = readAllTables()[tableId];
    if (!room) throw new Error('Table not found.');
    if (method === 'POST') {
      return {
        result: {
          winnerSeatIds: body?.winnerSeatIds ?? [],
          isDraw: Boolean(body?.isDraw),
          lockedAt: Date.now() + 86_400_000,
        },
        deckStrengths: {},
        syncedProviders: [],
      } as T;
    }
    return {
      tableId,
      format: room.state.settings.format,
      isTracked: room.state.settings.isTracked,
      turnNumber: room.state.turnNumber,
      winnerSeatIds: room.state.winnerSeatIds,
      isDraw: false,
      seats: room.state.seatOrder
        .map((sid, idx) => {
          const s = room.state.seats[sid];
          if (!s || !s.accountId) return null;
          return {
            seatId: sid,
            displayName: s.displayName,
            accountId: s.accountId,
            deckId: s.deckMeta?.deckId ?? null,
            eraNumber: 1,
            bracket: s.deckMeta?.detectedBracket ?? 2,
            eliminatedTurn: null,
            suggestedPlacement: idx + 1,
            isWinner: room.state.winnerSeatIds.includes(sid),
          };
        })
        .filter(Boolean),
      existingReport: null,
    } as T;
  }

  if (pathname === '/api/decks/parse' && method === 'POST') {
    return parsePlainTextDeck(
      String(body?.text ?? ''),
      String(body?.name ?? 'Imported Deck'),
      (body?.format as FormatId) ?? 'commander'
    ) as T;
  }

  if (pathname === '/api/decks/import' && method === 'POST') {
    const res = await importUniversalDeck(String(body?.rawInput ?? ''), {
      filename: body?.filename,
      deckName: body?.deckName,
      format: body?.format,
      csvMapping: body?.csvMapping,
      forceSource: body?.forceSource,
    });
    return res as T;
  }

  if (pathname === '/api/decks/export' && method === 'POST') {
    const text = exportDeckToFormat(body.deck, body.exportFormat ?? 'arena');
    return { text, exportFormat: body.exportFormat ?? 'arena' } as T;
  }

  if (pathname === '/api/cards/search') {
    const q = (qs.get('q') ?? '').trim().toLowerCase();
    const localHits = SEED_CARD_CATALOG.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 20);
    if (qs.get('live') === '1' && q.length >= 2) {
      try {
        const res = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}`);
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json?.data)) {
            const liveCards = json.data.slice(0, 24).map((raw: Record<string, unknown>) =>
              normalizeScryfallApiCard(raw)
            );
            return { cards: liveCards } as T;
          }
        }
      } catch {
        // fall back to local catalog
      }
    }
    return { cards: localHits } as T;
  }

  if (pathname === '/api/decks') {
    if (method === 'GET') {
      return { decks: readSavedDecks() } as T;
    }
    if (method === 'POST') {
      const profile = ensureLocalProfile();
      const decks = readSavedDecks();
      const deckPayload: ImportedDeckPayload = body.deck;
      const fp = computeDeckFingerprint(deckPayload);
      const now = Date.now();
      const existingIdx = body.deckId ? decks.findIndex((d) => d.deckId === body.deckId) : -1;
      const saved: SavedDeckDTO = {
        deckId: existingIdx >= 0 ? decks[existingIdx].deckId : randomId('dck', 10),
        accountId: profile.accountId,
        shareSlug: existingIdx >= 0 ? decks[existingIdx].shareSlug : randomId('shr', 8),
        name: deckPayload.name || 'Saved Deck',
        format: deckPayload.format ?? 'commander',
        deck: deckPayload,
        cosmetics: body.cosmetics ?? { sleeveCardName: null, playmatCardName: null, focalPoint: 'center' },
        exactHash: fp.exactHash,
        eraNumber: existingIdx >= 0 ? decks[existingIdx].eraNumber : 1,
        trackedGames: existingIdx >= 0 ? decks[existingIdx].trackedGames : 0,
        isPublic: Boolean(body.isPublic ?? true),
        createdAt: existingIdx >= 0 ? decks[existingIdx].createdAt : now,
        updatedAt: now,
      };
      if (existingIdx >= 0) decks[existingIdx] = saved;
      else decks.unshift(saved);
      writeSavedDecks(decks);
      return { savedDeck: saved } as T;
    }
  }

  const deleteDeckMatch = pathname.match(/^\/api\/decks\/([^/]+)$/);
  if (deleteDeckMatch && method === 'DELETE') {
    const id = decodeURIComponent(deleteDeckMatch[1]);
    writeSavedDecks(readSavedDecks().filter((d) => d.deckId !== id));
    return { ok: true } as T;
  }

  if (pathname === '/api/cart/optimize' && method === 'POST') {
    const items: CartDeckItem[] = body?.items ?? deckToCartItems(body?.deck, body?.includeSideboard ?? true);
    const quotes = seedVendorQuotesForItems(items);
    const result = optimizeDeckCart({
      items,
      quotes,
      ownedCollectionText: body?.ownedCollectionText,
      preferences: body?.preferences,
    });
    return { items, quotes, result } as T;
  }

  if (pathname === '/api/cart/reoptimize' && method === 'POST') {
    const items: CartDeckItem[] = body?.items ?? deckToCartItems(body?.deck, body?.includeSideboard ?? true);
    const quotes = body?.quotes ?? seedVendorQuotesForItems(items);
    const outcome = reoptimizeAfterStockCheck({
      items,
      quotes,
      previousResult: body.previousResult,
      stockUpdates: body.stockUpdates ?? [],
      ownedCollectionText: body?.ownedCollectionText,
      preferences: body?.preferences,
    });
    return outcome as T;
  }

  if (pathname === '/api/cart/alerts') {
    if (method === 'GET') {
      return { alerts: readPriceAlerts() } as T;
    }
    if (method === 'POST') {
      const items: CartDeckItem[] = body?.items ?? deckToCartItems(body?.deck, true);
      const res = optimizeDeckCart({ items, ownedCollectionText: body?.ownedCollectionText, preferences: body?.preferences });
      const now = Date.now();
      const alert: DeckPriceAlertDTO = {
        alertId: randomId('alt', 8),
        deckName: String(body?.deckName || 'Deck Alert'),
        targetTotalUsd: Number(body?.targetTotalUsd ?? 50),
        lastCheckedTotalUsd: res.grandTotalUsd,
        triggered: res.grandTotalUsd <= Number(body?.targetTotalUsd ?? 50),
        triggeredAt: res.grandTotalUsd <= Number(body?.targetTotalUsd ?? 50) ? now : null,
        createdAt: now,
        updatedAt: now,
      };
      const next = [alert, ...readPriceAlerts()];
      writePriceAlerts(next);
      return { alert } as T;
    }
  }

  const delAlertMatch = pathname.match(/^\/api\/cart\/alerts\/([^/]+)$/);
  if (delAlertMatch && method === 'DELETE') {
    const id = decodeURIComponent(delAlertMatch[1]);
    writePriceAlerts(readPriceAlerts().filter((a) => a.alertId !== id));
    return { ok: true } as T;
  }

  if (pathname === '/api/queues/status') {
    return { status: 'idle', queuePopulation: 0 } as T;
  }

  if (pathname.startsWith('/api/leaderboards/')) {
    const fmt = (decodeURIComponent(pathname.split('/').pop() ?? 'commander') as FormatId);
    return { format: fmt, entries: [], myStanding: null } as T;
  }

  if (pathname === '/api/calendar') {
    return { games: [] } as T;
  }

  if (pathname === '/api/account/tracking-webhooks') {
    return { webhooks: [] } as T;
  }

  return { ok: true } as T;
}
